import {
  FormField,
  FormGrid,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  Text,
} from '@dixa/lib-shared-habla';
import { ApiErrorStatusCode, isResponseWithData } from 'lib-shared-auth';
import noop from 'lodash/noop';
import { useCallback } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

import { intentDetectionApi } from 'apis/intentDetection';
import { HookFormText, HookFormTextarea } from 'components/hookForm';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { createAppModal } from 'utils/modal';

import styles from './intentDetectionModal.module.scss';

export type FieldValues = {
  name: string;
  description: string;
};

/** A component that displays edit intent detection modal. */
export const IntentDetectionModal = createAppModal(
  { type: 'settings:intentDetection' },
  ({ isOpen, hide, modal }): JSX.Element => {
    const { intentDetection } = modal.arg;
    const notify = useNotifications();

    const { formState, control, handleSubmit } = useForm<FieldValues>({
      defaultValues: {
        name: intentDetection?.name,
        description: intentDetection?.description,
      },
      mode: 'all',
    });

    /** Handles closing the modal. */
    const handleClose = useCallback(() => {
      hide?.();
    }, [hide]);

    const [createIntentDetection] =
      intentDetectionApi.useCreateIntentDetectionMutation({});

    const [updateIntentDetection] =
      intentDetectionApi.useUpdateIntentDetectionMutation();

    const handleUpsertIntentDetection = useCallback(
      async (fieldValues: FieldValues) => {
        try {
          if (intentDetection) {
            const updateResponse = await updateIntentDetection({
              id: intentDetection.id,
              name: fieldValues.name,
              description: fieldValues.description,
            }).unwrap();
            if (isResponseWithData(updateResponse)) {
              handleClose();
            }
          } else {
            const createResponse = await createIntentDetection({
              name: fieldValues.name,
              description: fieldValues.description,
            }).unwrap();

            if (isResponseWithData(createResponse)) {
              handleClose();
            }
          }
        } catch (e) {
          const error = e as unknown as {
            status: number;
            data: {
              errors: string[];
            };
          };

          notify(
            {
              title:
                error && error.status === ApiErrorStatusCode.Conflict
                  ? 'Intent name already exists.'
                  : 'Something went wrong.',
            },
            NotificationType.Error,
          );
        }
      },
      [
        createIntentDetection,
        handleClose,
        intentDetection,
        notify,
        updateIntentDetection,
      ],
    );

    const handleValidSubmit: SubmitHandler<FieldValues> = async (
      fieldValues,
    ) => {
      await handleUpsertIntentDetection(fieldValues);
    };

    return (
      <Modal isOpen={isOpen} onClose={handleClose} className={styles.modal}>
        <form
          noValidate
          tabIndex={-1}
          onSubmit={handleSubmit(handleValidSubmit)}
        >
          <ModalHead
            title={
              <Text.Heading>
                {intentDetection
                  ? 'Edit intent detection'
                  : 'Add intent detection'}
              </Text.Heading>
            }
            onClose={handleClose}
          />

          <ModalBody>
            <FormGrid>
              <FormField>
                <Text.Paragraph withMarginBottom variant="2">
                  Use AI to classify the purpose and intention of inbound
                  communications.
                </Text.Paragraph>
                <HookFormText
                  name="name"
                  label="Name of the intent"
                  control={control}
                  componentProps={{
                    placeholder: 'Angry Customer',
                  }}
                  rules={{
                    required: 'Intent detection name is required',
                  }}
                ></HookFormText>
              </FormField>
              <FormField>
                <HookFormTextarea
                  name="description"
                  label="Goal"
                  control={control}
                  componentProps={{
                    placeholder:
                      'Spot an angry customer by their negative sentiment, harsh language, use of CAPS, exclamations, etc.',
                  }}
                  helpIcon={{
                    text: (
                      <>
                        Describe in simple terms what you want to achieve with
                        this intent detection.
                      </>
                    ),
                  }}
                  rules={{
                    required: 'Intent detection description is required',
                  }}
                ></HookFormTextarea>
              </FormField>
            </FormGrid>
          </ModalBody>

          <ModalFoot
            cancelOnClick={handleClose}
            confirmName={intentDetection ? 'Save' : 'Add'}
            confirmOnClick={noop} // A hack to show confirm button
            confirmProps={{
              disabled: !(formState.isValid && formState.isDirty),
              type: 'submit',
              isLoading: formState.isSubmitting,
            }}
          />
        </form>
      </Modal>
    );
  },
);
